<template>
  <div class="bulk-actions-wrap">
    <!-- show number of selected actions with a clear button -->
    <div class="multi-selected-cards-wrap box-border h-[26px]">
      <em
        class="custom-icons custom mr-1 bg-attention-icon ml-1"
        :style="getCustomIconStyle(iconPath('card-icon'))"
      />
      <span class="text-sm text-important-text mr-1">
        <span class="min-w-6 inline-flex items-center justify-center">{{
          multiSelectCards.length
        }}</span>
        {{ (multiSelectCards.length === 1 ? translate('card') : translate('cards')).toLowerCase() }}
        {{ translate('selected').toLowerCase() }}
      </span>
      <SuggestionSpinner
        v-if="globalState.bulkActionsInProgress"
        class="ml-4 mr-3"
      />
      <v-popover :offset="[0, 14]">
        <ButtonCustom
          :type="'attention clear-cards-selection'"
          :custom-icon-name="'x'"
          :custom-icon-color="'--var(attentionIcon)'"
          :custom-icon-size="'16px'"
          :button-dimensions="{ height: '24px' }"
          @click="xButtonHandler"
        />
        <template #content>
          <TooltipTemplate
            :tooltip-text="xTooltipText"
            :tooltip-meta="{ icon: { key: 'Esc' } }"
          />
        </template>
      </v-popover>
    </div>
    <div class="divider-line"></div>
    <v-popover
      v-if="!(isInEpicBoard || isEpicsBoard)"
      data-test-move-to-board
      interactive
      tag="div"
      content-tag="div"
      :role="'poper'"
      placement="top-start"
      :trigger="'click'"
      :append-to="body"
      :offset="[0, 14]"
      :on-show="handleOnShow"
    >
      <template #default="{ state }">
        <ButtonCustom
          :text="translate('moveToBoard')"
          :type="'attention bulk-actions-button mr-2'"
          :custom-icon-name="'arrow-right'"
          :custom-icon-size="'16px'"
          :custom-icon-color="'--var(attentionIcon)'"
          :button-dimensions="{ height: '24px' }"
          :disabled="globalState.bulkActionsInProgress"
        >
          <template #rightIcon>
            <em
              class="button-icon custom-icons custom ml-1 mr-0"
              :style="bulkActionsIcon(state.isVisible)"
            />
          </template>
        </ButtonCustom>
      </template>
      <template #content="{ state, hide }">
        <BoardFilterDropdown
          v-show="state.isVisible"
          ref="boardDropdown"
          :selected-board-id="''"
          class="template-card-board-popover top-7"
          @item-hover="handleBoardItemHover"
          @selectBoard="handleMoveCardToBoard($event, hide)"
          @hidePopup="hide"
        />
      </template>
    </v-popover>
    <v-popover
      interactive
      tag="div"
      content-tag="div"
      placement="top-start"
      trigger="click"
      :role="'poper'"
      :append-to="body"
      :offset="[0, 14]"
      :on-show="handleOnShow"
      :on-hide="handleOnHide"
    >
      <template #default="{ state }">
        <ButtonCustom
          :text="translate('moreActions')"
          :type="'attention bulk-actions-button'"
          :custom-icon-name="'more-horizontal'"
          :custom-icon-size="'16px'"
          :custom-icon-color="'--var(attentionIcon)'"
          :button-dimensions="{ height: '24px' }"
          :disabled="globalState.bulkActionsInProgress"
        >
          <template #rightIcon>
            <em
              class="button-icon custom-icons custom ml-1 mr-0"
              :style="bulkActionsIcon(state.isVisible)"
            />
          </template>
        </ButtonCustom>
      </template>

      <template #content="{ hide, state }">
        <ListDropdown
          v-if="state.isVisible && !selectedSubMenu"
          ref="dropdown"
          :items="bulkActionsDropdownItems"
          @click="handleBulkActionsClick($event, hide)"
          @closeEscape="hide"
        />
        <ListCardStatusPicker
          v-if="state.isVisible && selectedSubMenu === 'changeStatus'"
          :custom-statuses-list="boardLists"
          :show-dropdown-label="false"
          :allow-color-change="false"
          @click="($event) => handleChangeStatus($event, hide)"
          @close="hide"
          @esc="hide"
        />
        <FilterDropdown
          v-if="state.isVisible && selectedSubMenu === 'assignees'"
          :search-results="bulkMembers"
          :placeholder="`${translate('assignees')}...`"
          :hide-filter-popup="hide"
          @item-click="handleMemberSelected($event, hide)"
          @item-right-side-click="handleMemberSelected($event, hide)"
          @query-change="handleBulkMembersQueryChange"
          @escape="hide"
        />
        <DateTimePicker
          v-if="state.isVisible && selectedSubMenu === 'startDate'"
          :selected-date="bulkSelectedStartDate"
          @dueDate="(date) => setStartDate(date, hide)"
          @esc="hide"
        />
        <DateTimePicker
          v-if="state.isVisible && selectedSubMenu === 'dueDate'"
          :selected-date="bulkSelectedDueDate"
          @dueDate="(date) => setDueDate(date, hide)"
          @esc="hide"
        />
        <FilterDropdown
          v-if="state.isVisible && selectedSubMenu === 'tags'"
          :search-results="bulkTags"
          :placeholder="`${translate('tags')}...`"
          :hide-filter-popup="hide"
          @item-click="handleTagClick($event, hide)"
          @item-right-side-click="handleTagClick($event, hide)"
          @query-change="handleBulkTagsQueryChange"
          @escape="hide"
        />
      </template>
    </v-popover>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useBrowserLocation, useEventListener } from '@vueuse/core';

// components
import ButtonCustom from '../elements/ButtonCustom.vue';
import ListCardStatusPicker from './ListCardStatusPicker.vue';
import ListDropdown from './ListDropdown.vue';
import SuggestionSpinner from '../elements/SuggestionSpinner.vue';
import DateTimePicker from './DateTimePicker.vue';
import FilterDropdown from './filters/FilterDropdown.vue';
import BoardFilterDropdown from './BoardFilterDropdown.vue';
import TooltipTemplate from '../elements/TooltipTemplate.vue';

// utilities
import useCustomIcon from '../../utilities/composables/useCustomIcon';
import useTranslate from '../../utilities/composables/useTranslate';
import useBoardLists from '../../utilities/composables/useBoardLists';
import useBulkActions from '../../utilities/composables/useBulkActions';
import { globalState } from '../../utilities/globalState';
import { isBoardFullyLoaded } from '../../utilities/isFullyLoaded';
import constants from '../../utilities/constants';
import { EpicTabId } from '../../types/tabs';
import capitalizeFirstLetter from '../../utilities/capitalizeFirstLetter';

const { translate } = useTranslate();
const { getCustomIconStyle, iconPath } = useCustomIcon();

const store = useStore();
const route = useRoute();
const router = useRouter();
const selectedSubMenu = ref<string | null>(null);
const body = ref(document.body);

const listIds = computed(() => store.getters.boardsListsIds[store.getters.currentBoardId] || []);
const { boardLists } = useBoardLists(listIds);
// reactive window location
const location = useBrowserLocation();

const isInEpicBoard = computed(() => {
  const realRoute = router.resolve(location.value.pathname || '');
  return (
    realRoute.name === constants.epicRoute.name &&
    realRoute.params?.selectedTabId === EpicTabId.Cards
  );
});

const isEpicsBoard = computed(
  () => route.name === constants.epicsBoardRoute.name && !isInEpicBoard.value
);

const {
  allCardsAlreadyFavourited,
  allCardsSubscribed,
  bulkMembers,
  bulkSelectedDueDate,
  bulkSelectedStartDate,
  bulkTags,
  bulkTagChange,
  clearMultiSelectedCardIds,
  bulkChangeStatus,
  bulkSetCardDates,
  multiSelectCards,
  bulkMemberChange,
  bulkAddFavourite,
  bulkRemoveFavourite,
  bulkSubscribe,
  bulkMoveCardToBoard,
  bulkMembersSearchValue,
  bulkTagsSearchValue,
  copyCardTitlesToClipboard,
  copyCardTitlesAsLinksToClipboard,
  stopBulkAction,
} = useBulkActions();

const escListener = useEventListener('keydown', (e) => {
  if (e.key === 'Escape' && globalState.bulkActionsInProgress) {
    stopBulkAction();
  }
});

onUnmounted(() => {
  escListener();
});

const bulkActionsIcon = (isVisible: boolean) => {
  return getCustomIconStyle(iconPath(isVisible ? 'chevron-up' : 'chevron-down'));
};

const xTooltipText = computed(() => {
  return globalState.bulkActionsInProgress
    ? capitalizeFirstLetter(translate('stop'))
    : translate('clearSelection');
});

const handleOnShow = () => {
  if (globalState.bulkActionsInProgress) return false;
};

const handleOnHide = () => {
  if (bulkMembersSearchValue.value) bulkMembersSearchValue.value = '';
  if (bulkTagsSearchValue.value) bulkTagsSearchValue.value = '';
  selectedSubMenu.value = null;
};

const bulkActionsDropdownItems = computed(() => {
  return [
    {
      id: allCardsSubscribed.value ? 'unsubscribe' : 'subscribe',
      label: translate(allCardsSubscribed.value ? 'unsubscribe' : 'subscribe'),
      customIconName: iconPath(allCardsSubscribed.value ? 'bell-off' : 'bell'),
      disabled: false,
    },
    {
      id: allCardsAlreadyFavourited.value ? 'unfavourite' : 'favourite',
      label: translate(allCardsAlreadyFavourited.value ? 'unfavourite' : 'favourite'),
      customIconName: iconPath(allCardsAlreadyFavourited.value ? 'unfavourite' : 'star'),
      disabled: false,
    },
    ...(!isInEpicBoard.value
      ? [
          {
            id: 'changeStatus',
            label: translate('changeStatus'),
            customIconName: iconPath('status'),
            disabled: false,
          },
        ]
      : []),
    {
      id: 'assignees',
      label: translate('assignees'),
      customIconName: iconPath('user-plus'),
    },
    {
      id: 'tags',
      label: translate('tags'),
      customIconName: iconPath('tag'),
    },
    {
      id: 'startDate',
      label: translate('startDate'),
      customIconName: iconPath('calendar'),
    },
    {
      id: 'dueDate',
      label: translate('dueDate'),
      customIconName: iconPath('calendar'),
    },
    {
      id: 'copyCardTitles',
      label: translate('copyTitles'),
      customIconName: iconPath('copy'),
    },
    {
      id: 'copyCardTitlesAsLinks',
      label: translate('copyTitlesAsLinks'),
      customIconName: iconPath('link-st'),
    },
  ];
});

const handleChangeStatus = (status: any, hide: () => void) => {
  hide?.();
  bulkChangeStatus(status);
};

const handleMemberSelected = (member: any, hide: () => void) => {
  hide();
  bulkMemberChange(member);
};

const setStartDate = (date: Date, hide: () => void) => {
  hide?.();
  bulkSetCardDates('start_date', date);
};

const setDueDate = (date: Date, hide: () => void) => {
  hide?.();
  bulkSetCardDates('due_date', date);
};

const handleTagClick = (tag: any, hide: () => void) => {
  hide();
  bulkTagChange(tag);
};

const handleMoveCardToBoard = (item: Record<string, any>, hide: () => void) => {
  hide();
  bulkMoveCardToBoard(item.board.id);
};

const handleBulkActionsClick = (item: any, hide: () => void) => {
  switch (item.id) {
    case 'subscribe':
      bulkSubscribe(true);
      hide();
      break;
    case 'unsubscribe':
      bulkSubscribe(false);
      hide();
      break;
    case 'favourite':
      bulkAddFavourite();
      hide();
      break;
    case 'unfavourite':
      bulkRemoveFavourite();
      hide();
      break;
    case 'copyCardTitles':
      copyCardTitlesToClipboard();
      hide();
      break;
    case 'copyCardTitlesAsLinks':
      copyCardTitlesAsLinksToClipboard();
      hide();
      break;
    default:
      selectedSubMenu.value = item.id;
      break;
  }
};

const handleBoardItemHover = (item: any) => {
  const board = store.getters.getFlatBoards[item.id];
  if (!isBoardFullyLoaded(board)) {
    store.dispatch('fetchBoard', { boardId: item.id });
  }
};

const xButtonHandler = () => {
  if (globalState.bulkActionsInProgress) {
    stopBulkAction();
  } else {
    clearMultiSelectedCardIds();
  }
};

const handleBulkMembersQueryChange = ({ value }: { value: string }) => {
  bulkMembersSearchValue.value = value;
};

const handleBulkTagsQueryChange = ({ value }: { value: string }) => {
  bulkTagsSearchValue.value = value;
};
</script>

<style lang="scss">
.bulk-actions-wrap {
  @apply flex
  items-center
  rounded-lg
  shadow-lg
  shadow-gray-500
  p-3
  absolute
  bottom-8
  right-0
  left-0
  mx-auto
  w-fit
  bg-attention-bg
  text-attention-text
  z-20;

  .button-icon {
    @apply bg-attention-icon;
  }

  .divider-line {
    @apply border-0 border-l border-solid border-standard-border mr-2 h-6;
  }
}

.multi-selected-cards-wrap {
  @apply flex items-center mr-2 border border-dashed border-standard-border rounded;

  .clear-cards-selection {
    @apply rounded-l-none rounded-r ml-1;
    em {
      @apply mr-0 !important;
    }

    &.disabled {
      @apply bg-attention-bg text-text-default border-0 cursor-not-allowed;
      em {
        @apply bg-icon-default;
      }
    }
  }

  .spinner {
    &:after {
      background: var(--attentionBg);
    }
  }
}

.bulk-actions-button {
  @apply border border-solid border-standard-border rounded;

  &.attention {
    &.disabled {
      @apply bg-attention-bg text-text-default border-important-border;
      em {
        @apply bg-icon-default;
      }
    }
  }
}
</style>
